import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Pencil, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';
import GalleryCardDialog from './GalleryCardDialog';

const emptyDraft = { id: null, title: '', category: '', description: '', images: [], visible: true };


const AdminGallery = ({ onUnauthorized }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/gallery/all`, { headers: authHeaders() });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error(`Failed to load: ${apiErr(err)}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUnauthorized]); // API/apiErr/authHeaders are module-scoped imports and stable

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setDraft(emptyDraft); setDialogOpen(true); };
  const openEdit = (it) => {
    setDraft({
      id: it.id, title: it.title, category: it.category,
      description: it.description || '', images: it.images || [], visible: it.visible !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        category: draft.category.trim(),
        description: draft.description,
        images: draft.images,
        visible: draft.visible,
      };
      if (draft.id) {
        await axios.put(`${API}/api/gallery/${draft.id}`, payload, { headers: authHeaders() });
        toast.success('Card updated');
      } else {
        await axios.post(`${API}/api/gallery`, payload, { headers: authHeaders() });
        toast.success('Card created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (it) => {
    try {
      await axios.put(`${API}/api/gallery/${it.id}`, { visible: !it.visible }, { headers: authHeaders() });
      await load();
    } catch (err) {
      toast.error(`Toggle failed: ${apiErr(err)}`);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/api/gallery/${deleteTarget.id}`, { headers: authHeaders() });
      toast.success('Card deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(`Delete failed: ${apiErr(err)}`);
    }
  };

  return (
    <section data-testid="admin-gallery-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold">Gallery cards</h2>
          <p className="text-sm text-gray-400">{items.length} total · {items.filter(i => i.visible).length} visible</p>
        </div>
        <Button
          data-testid="admin-new-card"
          onClick={openNew}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> New card
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-cards-grid">
          {items.map((it) => (
            <Card key={it.id} className="bg-slate-900 border-slate-800 overflow-hidden" data-testid={`admin-card-${it.id}`}>
              <div className="relative h-44 bg-slate-800">
                {it.images?.[0] ? (
                  <img src={it.images[0]} alt={it.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">No images</div>
                )}
                {!it.visible && (
                  <div className="absolute top-2 left-2 bg-black/70 text-yellow-400 text-xs px-2 py-1 rounded flex items-center gap-1">
                    <EyeOff className="w-3 h-3" /> Hidden
                  </div>
                )}
                {it.images?.length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {it.images.length} photos
                  </div>
                )}
              </div>
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-white leading-snug">{it.title}</CardTitle>
                <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30 w-fit">
                  {it.category}
                </Badge>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-400 line-clamp-2 min-h-[2.5rem]">{it.description}</p>
                <div className="flex items-center gap-2 mt-4">
                  <Button data-testid={`admin-edit-${it.id}`} size="sm" variant="outline" onClick={() => openEdit(it)} className="border-slate-700 text-white hover:bg-slate-800">
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button data-testid={`admin-toggle-${it.id}`} size="sm" variant="outline" onClick={() => toggleVisible(it)} className="border-slate-700 text-white hover:bg-slate-800">
                    {it.visible ? <><EyeOff className="w-3 h-3 mr-1" /> Hide</> : <><Eye className="w-3 h-3 mr-1" /> Show</>}
                  </Button>
                  <Button data-testid={`admin-delete-${it.id}`} size="sm" variant="outline" onClick={() => setDeleteTarget(it)} className="border-red-900/60 text-red-400 hover:bg-red-950/40">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <GalleryCardDialog open={dialogOpen} onOpenChange={setDialogOpen} draft={draft} setDraft={setDraft} onSave={save} saving={saving} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this card?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              &ldquo;{deleteTarget?.title}&rdquo; will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-delete-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default AdminGallery;
